/**
 * Version Guard — detects stale frontend bundles and triggers a safe reload.
 *
 * Reads the build fingerprint from <meta name="app-version">, polls /api/version
 * every POLL_INTERVAL_MS, and reloads the page when a newer version is deployed —
 * but only when safeToReload is true.
 *
 * Game pages opt in to the "unsafe" state to prevent reloads mid-game:
 *   window.__versionGuard?.setSafeToReload(false);  // game starting
 *   window.__versionGuard?.setSafeToReload(true);   // game ended
 */

import { logger } from './logger.js';

const POLL_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const VERSION_ENDPOINT = '/api/version';

// ─── Public API (set on window for game pages to call without import) ────────

interface VersionGuardAPI {
  setSafeToReload(safe: boolean): void;
  stop(): void;
}

declare global {
  interface Window {
    __versionGuard?: VersionGuardAPI;
    __versionGuard_checkNow?: () => Promise<void>;
  }
}

// ─── Internal state ──────────────────────────────────────────────────────────

const knownVersion: string = (
  document.querySelector<HTMLMetaElement>('meta[name="app-version"]')?.content ?? ''
);

let safeToReload = true;
let pendingReload = false;
let pollTimer: ReturnType<typeof setTimeout> | null = null;

// ─── Core logic ──────────────────────────────────────────────────────────────

function doReload(): void {
  logger.info('[VersionGuard] Reloading — new version available');
  window.location.reload();
}

async function checkVersion(): Promise<void> {
  if (!knownVersion) return; // No meta tag — guard disabled

  try {
    const res = await fetch(VERSION_ENDPOINT, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!res.ok) return;

    const data = await res.json() as { version: string };

    if (data.version && data.version !== knownVersion) {
      logger.info(`[VersionGuard] Stale (have: ${knownVersion}, latest: ${data.version})`);
      pendingReload = true;
      if (safeToReload) {
        doReload();
      }
    }
  } catch {
    // Network error — ignore, retry next cycle
  }
}

function scheduleNextPoll(): void {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = setTimeout(async () => {
    if (document.visibilityState !== 'hidden') {
      await checkVersion();
    }
    scheduleNextPoll();
  }, POLL_INTERVAL_MS);
}

// ─── Public API ──────────────────────────────────────────────────────────────

function setSafeToReload(safe: boolean): void {
  safeToReload = safe;
  if (safe && pendingReload) {
    doReload();
  }
}

function stop(): void {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}

// ─── Initialization ──────────────────────────────────────────────────────────

// Reload immediately if the tab was hidden and comes back stale
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    if (pendingReload && safeToReload) {
      doReload();
    } else if (!pendingReload) {
      // Tab just became visible — do an eager check
      void checkVersion();
    }
  }
});

const versionGuard: VersionGuardAPI = { setSafeToReload, stop };
window.__versionGuard = versionGuard;

// Expose checkNow for E2E and integration tests
window.__versionGuard_checkNow = checkVersion;

scheduleNextPoll();
